import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import { Loader2 } from "lucide-react";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline" | "warning";
type Size = "sm" | "md" | "lg";

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const V: Record<Variant, string> = {
  primary:   "bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-elevate-1",
  secondary: "bg-surface-100 text-surface-900 hover:bg-surface-200 active:bg-surface-300",
  ghost:     "bg-transparent text-surface-700 hover:bg-surface-100 active:bg-surface-200",
  danger:    "bg-danger-600 text-white hover:bg-danger-700 active:bg-danger-700 shadow-elevate-1",
  outline:   "bg-white border border-surface-300 text-surface-800 hover:bg-surface-50 active:bg-surface-100",
  warning:   "bg-warning-600 text-white hover:bg-warning-700 active:bg-warning-800 shadow-elevate-1",
};

const S: Record<Size, string> = {
  sm: "h-8 px-3 text-sm rounded-md gap-1.5",
  md: "h-10 px-4 text-sm rounded-lg gap-2",
  lg: "h-12 px-6 text-base rounded-lg gap-2",
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = "primary", size = "md", className, loading, disabled,
    leftIcon, rightIcon, fullWidth, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={twMerge(
        clsx(
          "inline-flex items-center justify-center font-medium transition-all",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          V[variant], S[size], fullWidth && "w-full", className,
        ),
      )}
      {...rest}
    >
      {loading
        ? <Loader2 className="h-4 w-4 animate-spin" />
        : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});
